// lpComponentTypes.ts
// Defines all LP component types, per-type props interfaces, the component palette,
// and the default AI generation order for landing pages.

// ---------------------------------------------------------------------------
// 1. Component type union
// ---------------------------------------------------------------------------

export type LPComponentType =
  | 'lp_hero'
  | 'lp_features'
  | 'lp_services'
  | 'lp_stats'
  | 'lp_process'
  | 'lp_testimonials'
  | 'lp_faq'
  | 'lp_quiz'
  | 'lp_lead_form'
  | 'lp_cta'
  | 'lp_pricing'
  | 'lp_logo_cloud'
  | 'lp_video'
  | 'lp_scarcity_timer'
  | 'lp_vsl'
  | 'lp_stock'
  | 'lp_exit_popup'
  | 'lp_social_proof_popup'
  | 'lp_authority'
  | 'lp_roi_calculator'
  | 'lp_header'
  | 'lp_footer'
  | 'lp_product'
  | 'lp_whitepaper'
  | 'lp_share'
  | 'lp_compliance_banner'
  | 'lp_cookie_consent_banner'
  | 'lp_text'
  | 'lp_effect';

/** All valid LP component type strings as a runtime array (useful for validation). */
export const LP_COMPONENT_TYPES: LPComponentType[] = [
  'lp_hero',
  'lp_features',
  'lp_services',
  'lp_stats',
  'lp_process',
  'lp_testimonials',
  'lp_faq',
  'lp_quiz',
  'lp_lead_form',
  'lp_cta',
  'lp_pricing',
  'lp_logo_cloud',
  'lp_video',
  'lp_scarcity_timer',
  'lp_vsl',
  'lp_stock',
  'lp_exit_popup',
  'lp_social_proof_popup',
  'lp_authority',
  'lp_roi_calculator',
  'lp_header',
  'lp_footer',
  'lp_product',
  'lp_whitepaper',
  'lp_share',
  'lp_compliance_banner',
  'lp_cookie_consent_banner',
  'lp_text',
  'lp_effect',
];

// ---------------------------------------------------------------------------
// 2. Per-type props interfaces
// ---------------------------------------------------------------------------

export interface LpHeroProps {
  headline: string;
  subheadline: string;
  badge: string;
  cta_text: string;
  cta_url: string;
  secondary_cta_text: string;
  secondary_cta_url: string;
  background_style: 'gradient' | 'image' | 'solid';
  background_image: string;
  layout: 'centered' | 'split_form' | 'split_image';
  form_title?: string;
  form_submit_text?: string;
  form_id?: string | null;
  form_success_message?: string | null;
}

export interface LpFeaturesProps {
  title: string;
  items: Array<{ icon: string; title: string; description: string }>;
}

export interface LpServicesProps {
  title: string;
  items: Array<{ icon: string; title: string; description: string }>;
}

export interface LpStatsProps {
  items: Array<{ value: string; label: string }>;
}

export interface LpProcessProps {
  title: string;
  steps: string[];
}

export interface LpTestimonialsProps {
  title: string;
  items: Array<{ quote: string; author: string; role: string; company?: string; avatar_url?: string }>;
}

export interface LpFaqProps {
  title: string;
  items: Array<{ question: string; answer: string }>;
}

export interface LpQuizProps {
  title: string;
  quiz_id: string;
  landing_page_id: string;
}

export interface LpLeadFormProps {
  title: string;
  subtitle: string;
  landing_page_id: string;
  submit_label: string;
  form_id?: string | null;
}

export interface LpCtaProps {
  title: string;
  subtitle: string;
  button_text: string;
  button_url: string;
  display_mode?: 'button' | 'inline_form';
  form_id?: string | null;
  form_title?: string;
  submit_button_text?: string;
  button_color?: string;
  button_shadow_color?: string;
  success_message?: string;
}

export interface LpPricingProps {
  title: string;
  items: any[];
}

export interface LpLogoCloudProps {
  title: string;
  logos: any[];
}

export interface LpVideoProps {
  title: string;
  video_url: string;
  thumbnail_url: string;
}

export interface LpScarcityTimerProps {
  title: string;
  subtitle: string;
  end_datetime: string;
  expired_message: string;
  background_color: string;
  show_days: boolean;
}

export interface LpVslProps {
  title: string;
  video_url: string;
  autoplay: boolean;
  hide_controls: boolean;
  poster_url: string;
  cta_text: string;
  cta_url: string;
  show_cta_after_seconds: number;
}

export interface LpStockProps {
  title: string;
  current_stock: number;
  max_stock: number;
  unit_label: string;
  urgency_threshold: number;
  show_progress_bar: boolean;
}

export interface LpExitPopupProps {
  headline: string;
  body_text: string;
  cta_text: string;
  cta_url: string;
  dismiss_label: string;
  trigger_after_seconds: number;
  show_once_per_session: boolean;
}

export interface LpSocialProofPopupProps {
  title: string;
  notifications: Array<{ name: string; action: string; location: string; time_ago: string }>;
  display_interval_seconds: number;
  show_avatar: boolean;
}

export interface LpAuthorityProps {
  title: string;
  subtitle: string;
  logos: Array<{ name: string; logo_url: string; width?: number }>;
  credentials: Array<{ icon: string; label: string }>;
  show_logos: boolean;
  show_credentials: boolean;
}

export interface LpRoiCalculatorProps {
  title: string;
  subtitle: string;
  inputs: Array<{ id: string; label: string; type: 'slider' | 'number'; min: number; max: number; default: number; prefix: string; suffix: string }>;
  formula: string;
  output_label: string;
  output_prefix: string;
  output_suffix: string;
  cta_text: string;
  cta_url: string;
}

export interface LpHeaderProps {
  logo_url: string;
  company_name: string;
  phone: string;
  nav_links: Array<{ label: string; url: string }>;
  cta_text: string;
  cta_url: string;
  sticky: boolean;
}

export interface LpFooterProps {
  company_name: string;
  tagline: string;
  copyright: string;
  links: Array<{ label: string; url: string }>;
  show_social: boolean;
  logo_url: string;
  phone: string;
  email: string;
  address: string;
  opening_hours: string;
}

export interface LpProductProps {
  title: string;
  subtitle: string;
  product_id: string;
  product_name: string;
  product_description: string;
  product_image: string;
  variant_id: string;
  variant_label: string;
  variant_price: number;
  currency: string;
  button_text: string;
  background_color: string;
  show_guarantee: boolean;
  guarantee_text: string;
  // Optional colour overrides — unset falls back to the previous look
  // (theme primary for price/button, fixed greys for text).
  title_color?: string;
  subtitle_color?: string;
  name_color?: string;
  description_color?: string;
  price_color?: string;
  button_color?: string;
  button_text_color?: string;
  guarantee_color?: string;
}

/** Whitepaper / gated PDF lead magnet — product-backed like LpProductProps,
 *  but delivers a downloadable file (generated via the Products whitepaper
 *  pipeline) instead of selling a variant. When `gated` is true the section
 *  renders a lead-capture form; the download link is only revealed after a
 *  successful submission (see LPComponentRenderer's lp_whitepaper branch). */
export interface LpWhitepaperProps {
  product_id: string;
  heading: string;
  subheading: string;
  button_label: string;
  gated: boolean;
  form_id?: string | null;
  cover_image_override?: string;
}

export interface LpShareProps {
  title: string;
  subtitle: string;
  share_text: string;
  platforms: string[];
  show_copy_link: boolean;
  button_style: 'filled' | 'outline' | 'icon_only';
}

export interface LpComplianceBannerProps {
  message: string; accept_text: string; decline_text: string;
  privacy_url: string; position: 'bottom' | 'top';
  jurisdiction: string; license_holder: string;
  show_risk_disclaimer: boolean; risk_disclaimer_text: string;
  afsl_number: string; show_wholesale_notice: boolean; wholesale_notice_text: string;
  show_past_performance: boolean; past_performance_text: string;
  show_anti_hawking: boolean; anti_hawking_text: string;
  sec_ria_number: string; finra_crd: string;
  show_investment_advice_notice: boolean; investment_advice_text: string;
  fca_number: string; mifid_auth_number: string; nca_name: string;
  mas_cms_number: string; show_accredited_investor: boolean; accredited_investor_text: string;
  sfc_license_number: string; sfc_license_type: string;
  fsa_registration_number: string; sebi_registration_number: string;
  fsc_registration_number: string; other_license_number: string; regulatory_body: string;
}

export interface LpCookieConsentBannerProps {
  message: string; accept_text: string; decline_text: string;
  manage_text: string; privacy_url: string;
  cookie_policy_url: string; position: 'bottom' | 'top';
}

/** Plain text content block — heading + body, filled manually or via AI regen.
 *  No theme-specific colours: the renderer inherits text colour from the
 *  published-page base styles (see LpTextRenderer). */
export interface LpTextProps {
  heading: string;                 // optional H2; empty = no heading rendered
  body: string;                    // multiline body; newlines preserved
  align: 'left' | 'center';
  max_width: 'narrow' | 'wide';    // narrow ≈ 720px reading column, wide ≈ 1100px
}

/** RevFX visual-effect section — a full-bleed animated background with an editable
 *  text overlay nested above it.
 *  IMPORTANT: effect_id MUST be a RevFX manifest `id` (e.g. 'snow', 'sunbeam'),
 *  NOT a component name ('GodRays') or a guessed label ('godRays'). The catalogue
 *  (revfxCatalogue.ts) resolves the import path + component from this id; an
 *  invalid id resolves to nothing and renders an empty effect. */
export interface LpEffectProps {
  effect_id: string;                 // manifest id, e.g. 'snow'
  effect_props: Record<string, any>; // per-effect props, seeded from manifest defaults
  // text overlay
  overlay_heading: string;
  overlay_subheading: string;
  overlay_show_cta: boolean;         // show/hide the CTA button
  overlay_cta_text: string;
  overlay_cta_url: string;
  overlay_cta_color: string;         // CTA button background colour
  overlay_cta_align: 'left' | 'center' | 'right';  // CTA button alignment within the block
  overlay_align: 'left' | 'center' | 'right';      // text + block horizontal alignment
  overlay_valign: 'top' | 'center' | 'bottom';     // block vertical position in the section
  overlay_color: string;             // overlay text colour (effects are often dark)
  min_height: number;                // section height in px
}

// ---------------------------------------------------------------------------
// 3. Mapped type — links type string to its props interface
// ---------------------------------------------------------------------------

export type LPComponentPropsMap = {
  lp_hero: LpHeroProps;
  lp_features: LpFeaturesProps;
  lp_services: LpServicesProps;
  lp_stats: LpStatsProps;
  lp_process: LpProcessProps;
  lp_testimonials: LpTestimonialsProps;
  lp_faq: LpFaqProps;
  lp_quiz: LpQuizProps;
  lp_lead_form: LpLeadFormProps;
  lp_cta: LpCtaProps;
  lp_pricing: LpPricingProps;
  lp_logo_cloud: LpLogoCloudProps;
  lp_video: LpVideoProps;
  lp_scarcity_timer: LpScarcityTimerProps;
  lp_vsl: LpVslProps;
  lp_stock: LpStockProps;
  lp_exit_popup: LpExitPopupProps;
  lp_social_proof_popup: LpSocialProofPopupProps;
  lp_authority: LpAuthorityProps;
  lp_roi_calculator: LpRoiCalculatorProps;
  lp_header: LpHeaderProps;
  lp_footer: LpFooterProps;
  lp_product: LpProductProps;
  lp_whitepaper: LpWhitepaperProps;
  lp_share: LpShareProps;
  lp_compliance_banner: LpComplianceBannerProps;
  lp_cookie_consent_banner: LpCookieConsentBannerProps;
  lp_text: LpTextProps;
  lp_effect: LpEffectProps;
};

// ---------------------------------------------------------------------------
// 4. LPComponent — the runtime shape of a component instance on the page
//    props is typed as Record<string, any> to avoid generic gymnastics in the
//    editor while preserving the per-type interfaces for targeted use.
// ---------------------------------------------------------------------------

export interface LPComponent {
  id: string;
  type: LPComponentType;
  props: Record<string, any>;
}

// ---------------------------------------------------------------------------
// 5. Palette definitions
// ---------------------------------------------------------------------------

export interface LPPaletteItem {
  type: LPComponentType;
  label: string;
  description: string;
  /** Lucide icon name (kebab-case) */
  icon: string;
  defaultProps: Record<string, any>;
  /** true if the AI populates this component automatically during generation */
  aiGenerated: boolean;
}

export const LP_COMPONENT_PALETTE: LPPaletteItem[] = [
  {
    type: 'lp_hero',
    label: 'Hero Section',
    description: 'Full-width hero with headline, subheadline and CTA button',
    icon: 'layout',
    aiGenerated: true,
    defaultProps: {
      headline: 'Your Compelling Headline Here',
      subheadline: 'A supporting statement that reinforces your value proposition.',
      badge: 'Trusted by Industry Leaders',
      cta_text: 'Get Your Free Consultation',
      cta_url: '#lead-form',
      secondary_cta_text: 'Learn More',
      secondary_cta_url: '#services',
      background_style: 'gradient',
      background_image: '',
      layout: 'centered',
      form_title: '',
      form_submit_text: '',
      form_id: null,
      form_success_message: null,
    },
  },
  {
    type: 'lp_features',
    label: 'Features / Benefits',
    description: 'Grid of key benefits or features with icons',
    icon: 'grid',
    aiGenerated: true,
    defaultProps: { title: 'Why Choose Us', items: [] },
  },
  {
    type: 'lp_services',
    label: 'Services',
    description: 'Cards showcasing your services or offerings',
    icon: 'briefcase',
    aiGenerated: true,
    defaultProps: { title: 'Our Services', items: [] },
  },
  {
    type: 'lp_stats',
    label: 'Stats / Metrics',
    description: 'Key numbers that build credibility',
    icon: 'bar-chart-2',
    aiGenerated: false,
    defaultProps: {
      items: [
        { value: '500+', label: 'Clients Served' },
        { value: '98%', label: 'Satisfaction Rate' },
        { value: '15+', label: 'Years Experience' },
        { value: '100%', label: 'Guaranteed' },
      ],
    },
  },
  {
    type: 'lp_process',
    label: 'How It Works',
    description: 'Step-by-step process explanation',
    icon: 'list-ordered',
    aiGenerated: false,
    defaultProps: {
      title: 'How It Works',
      steps: [
        { title: 'Free Consultation', description: 'Book a no-obligation session to discuss your needs.' },
        { title: 'Custom Strategy', description: 'We design a tailored plan for your goals.' },
        { title: 'Implementation', description: 'Our team executes with precision and care.' },
        { title: 'Results', description: 'Track measurable outcomes and ongoing success.' },
      ],
    },
  },
  {
    type: 'lp_testimonials',
    label: 'Testimonials',
    description: 'Client reviews and social proof',
    icon: 'message-square',
    aiGenerated: true,
    defaultProps: { title: 'What Our Clients Say', items: [] },
  },
  {
    type: 'lp_quiz',
    label: 'Interactive Quiz',
    description: 'Lead-qualifying assessment quiz',
    icon: 'help-circle',
    aiGenerated: false,
    defaultProps: { title: 'Find Out Where You Stand', quiz_id: '', landing_page_id: '' },
  },
  {
    type: 'lp_lead_form',
    label: 'Lead Capture Form',
    description: 'Contact / booking form for lead capture',
    icon: 'mail',
    aiGenerated: false,
    defaultProps: {
      title: 'Book Your Free Consultation',
      subtitle: "Fill in your details and we'll be in touch shortly.",
      landing_page_id: '',
      submit_label: 'Book Now',
      form_id: null,
    },
  },
  {
    type: 'lp_faq',
    label: 'FAQ',
    description: 'Frequently asked questions accordion',
    icon: 'help-circle',
    aiGenerated: true,
    defaultProps: { title: 'Frequently Asked Questions', items: [] },
  },
  {
    type: 'lp_cta',
    label: 'CTA Banner',
    description: 'Bold call-to-action section to close the page',
    icon: 'zap',
    aiGenerated: false,
    defaultProps: {
      title: 'Ready to Get Started?',
      subtitle: 'Schedule your free consultation today.',
      button_text: 'Book Your Free Consultation',
      button_url: '#lead-form',
    },
  },
  {
    type: 'lp_compliance_banner',
    label: 'Financial Services Banner',
    description: 'Jurisdiction-aware compliance bar — AFSL, FCA, SEC/FINRA, MiFID II, MAS, SFC and more',
    icon: 'shield-check',
    aiGenerated: false,
    defaultProps: {
      message: 'We use cookies to improve your experience.',
      accept_text: 'Accept All', decline_text: 'Decline',
      privacy_url: '/privacy', position: 'bottom',
      jurisdiction: 'australia', license_holder: '',
      show_risk_disclaimer: true,
      risk_disclaimer_text: 'All investments carry risk. Past performance is not a reliable indicator of future performance. You should obtain independent financial advice before making any investment decision.',
      afsl_number: '', show_wholesale_notice: false,
      wholesale_notice_text: 'This information is provided to wholesale clients only as defined under section 761G of the Corporations Act 2001 (Cth).',
      show_past_performance: false,
      past_performance_text: 'Past performance is not a reliable indicator of future performance and should not be relied upon when making investment decisions.',
      show_anti_hawking: false,
      anti_hawking_text: 'This document does not constitute an offer in any jurisdiction where, or to any person to whom, it would not be lawful to make such an offer.',
      sec_ria_number: '', finra_crd: '', show_investment_advice_notice: true,
      investment_advice_text: 'This material is for informational purposes only and does not constitute investment advice.',
      fca_number: '', mifid_auth_number: '', nca_name: '',
      mas_cms_number: '', show_accredited_investor: false,
      accredited_investor_text: 'This material is directed at accredited investors only as defined under the Securities and Futures Act 2001 (Singapore).',
      sfc_license_number: '', sfc_license_type: 'Type 1',
      fsa_registration_number: '', sebi_registration_number: '',
      fsc_registration_number: '', other_license_number: '', regulatory_body: '',
    },
  },
  {
    type: 'lp_cookie_consent_banner',
    label: 'Cookie Consent Banner',
    description: 'Simple GDPR / cookie consent bar — no financial services fields',
    icon: 'lock',
    aiGenerated: false,
    defaultProps: {
      message: "We use cookies to improve your experience and analyse site traffic. By clicking 'Accept All', you consent to our use of cookies.",
      accept_text: 'Accept All', decline_text: 'Decline',
      manage_text: '', privacy_url: '/privacy',
      cookie_policy_url: '', position: 'bottom',
    },
  },
  {
    type: 'lp_pricing',
    label: 'Pricing Table',
    description: 'Service tiers and pricing options',
    icon: 'credit-card',
    aiGenerated: false,
    defaultProps: { title: 'Our Pricing', items: [] },
  },
  {
    type: 'lp_logo_cloud',
    label: 'Logo Cloud / Trust Bar',
    description: 'Client logos or certification badges',
    icon: 'award',
    aiGenerated: false,
    defaultProps: {
      title: 'Trusted By',
      logos: [
        { name: 'Trustpilot', logo_url: '', width: 120 },
        { name: 'Industry Body', logo_url: '', width: 120 },
        { name: 'Certification', logo_url: '', width: 120 },
        { name: 'Publication', logo_url: '', width: 120 },
      ],
    },
  },
  {
    type: 'lp_video',
    label: 'Video Section',
    description: 'Embedded video with optional title',
    icon: 'play-circle',
    aiGenerated: false,
    defaultProps: { title: '', video_url: '', thumbnail_url: '' },
  },
  {
    type: 'lp_scarcity_timer',
    label: 'Scarcity Timer',
    description: 'Countdown clock creating urgency around a deadline',
    icon: 'timer',
    aiGenerated: false,
    defaultProps: {
      title: 'Limited Time Offer',
      subtitle: 'This offer expires in:',
      end_datetime: '',
      expired_message: 'This offer has expired. Contact us for current availability.',
      background_color: '#e53e3e',
      show_days: true,
    },
  },
  {
    type: 'lp_vsl',
    label: 'Video Sales Letter',
    description: 'Full-width video optimised for VSL-style conversion',
    icon: 'video',
    aiGenerated: false,
    defaultProps: {
      title: '',
      video_url: '',
      autoplay: false,
      hide_controls: false,
      poster_url: '',
      cta_text: 'Book Your Free Consultation',
      cta_url: '#lead-form',
      show_cta_after_seconds: 0,
    },
  },
  {
    type: 'lp_stock',
    label: 'Stock / Availability',
    description: 'Shows remaining spots or stock to create urgency',
    icon: 'package',
    aiGenerated: false,
    defaultProps: {
      title: 'Limited Availability',
      current_stock: 7,
      max_stock: 20,
      unit_label: 'consultation spots remaining',
      urgency_threshold: 5,
      show_progress_bar: true,
    },
  },
  {
    type: 'lp_exit_popup',
    label: 'Exit-Intent Popup',
    description: 'Popup triggered when a visitor is about to leave',
    icon: 'log-out',
    aiGenerated: false,
    defaultProps: {
      headline: "Wait — Don't Leave Yet!",
      body_text: 'Book a free 15-minute consultation before you go. No obligation.',
      cta_text: 'Yes, Book My Free Consultation',
      cta_url: '#lead-form',
      dismiss_label: "No thanks, I'll pass",
      trigger_after_seconds: 0,
      show_once_per_session: true,
    },
  },
  {
    type: 'lp_social_proof_popup',
    label: 'Social Proof Notification',
    description: '"John from Melbourne just booked" style real-time social proof',
    icon: 'bell',
    aiGenerated: false,
    defaultProps: {
      title: 'Recent Activity',
      notifications: [
        { name: 'Sarah M.', action: 'booked a consultation', location: 'Melbourne', time_ago: '2 hours ago' },
        { name: 'James T.', action: 'requested a callback', location: 'Sydney', time_ago: '4 hours ago' },
        { name: 'David K.', action: 'downloaded the guide', location: 'Brisbane', time_ago: '1 day ago' },
      ],
      display_interval_seconds: 5,
      show_avatar: true,
    },
  },
  {
    type: 'lp_authority',
    label: 'Authority / Trusted By',
    description: 'Logos, certifications and credentials that transfer trust',
    icon: 'shield-check',
    aiGenerated: false,
    defaultProps: {
      title: 'Trusted By Industry Leaders',
      subtitle: '',
      logos: [],
      credentials: [
        { icon: 'award', label: 'Industry Certified' },
        { icon: 'shield', label: 'Fully Licensed & Insured' },
        { icon: 'users', label: '500+ Satisfied Clients' },
        { icon: 'star', label: '5-Star Rated' },
      ],
      show_logos: true,
      show_credentials: true,
    },
  },
  {
    type: 'lp_roi_calculator',
    label: 'ROI Calculator',
    description: 'Interactive calculator that shows visitors their potential return',
    icon: 'calculator',
    aiGenerated: false,
    defaultProps: {
      title: 'Calculate Your ROI',
      subtitle: 'See how much you could save or earn',
      inputs: [
        { id: 'revenue', label: 'Annual Revenue', type: 'number', min: 0, max: 10000000, default: 500000, prefix: '$', suffix: '' },
        { id: 'cost', label: 'Current Monthly Spend', type: 'slider', min: 0, max: 50000, default: 5000, prefix: '$', suffix: '/mo' },
      ],
      formula: 'revenue * 0.15 - cost * 12',
      output_label: 'Estimated Annual Benefit',
      output_prefix: '$',
      output_suffix: '',
      cta_text: 'Get Your Custom Estimate',
      cta_url: '#lead-form',
    },
  },
  {
    type: 'lp_header',
    label: 'Header / Navigation',
    description: 'Logo, navigation links and CTA button',
    icon: 'navigation',
    aiGenerated: false,
    defaultProps: {
      logo_url: '',
      company_name: 'Your Company',
      phone: '',
      nav_links: [
        { label: 'Services', url: '#services' },
        { label: 'About', url: '#about' },
        { label: 'Contact', url: '#contact' },
      ],
      cta_text: 'Get Started',
      cta_url: '#lead-form',
      sticky: true,
    },
  },
  {
    type: 'lp_footer',
    label: 'Footer',
    description: 'Company info, links and copyright',
    icon: 'panel-bottom',
    aiGenerated: false,
    defaultProps: {
      company_name: 'Your Company',
      tagline: '',
      copyright: `© ${new Date().getFullYear()} Your Company. All rights reserved.`,
      links: [
        { label: 'Privacy Policy', url: '#' },
        { label: 'Terms of Service', url: '#' },
      ],
      show_social: false,
    },
  },
  {
    type: 'lp_product',
    label: 'Product / Buy Now',
    description: 'Sell a product with a one-click Stripe checkout button',
    icon: 'shopping-cart',
    aiGenerated: false,
    defaultProps: {
      title: 'Get Instant Access',
      subtitle: 'One-time payment — no subscription required.',
      product_id: '',
      product_name: '',
      product_description: '',
      product_image: '',
      variant_id: '',
      variant_label: '',
      variant_price: 0,
      currency: 'USD',
      button_text: 'Buy Now',
      background_color: '#ffffff',
      show_guarantee: true,
      guarantee_text: '30-day money-back guarantee',
    },
  },
  {
    type: 'lp_whitepaper',
    label: 'Whitepaper Download',
    description: 'Offer a generated PDF whitepaper — gate it behind a lead form or link it directly',
    icon: 'file-text',
    aiGenerated: false,
    defaultProps: {
      product_id: '',
      heading: 'Download Our Free Guide',
      subheading: 'Get instant access to our in-depth whitepaper.',
      button_label: 'Download Now',
      gated: true,
      form_id: null,
      cover_image_override: '',
    },
  },
  {
    type: 'lp_share',
    label: 'Social Share',
    description: 'Let visitors share this page on social media',
    icon: 'share-2',
    aiGenerated: false,
    defaultProps: {
      title: 'Share This Page',
      subtitle: 'Know someone who could benefit? Share it with them.',
      share_text: 'Check this out:',
      platforms: ['facebook', 'twitter', 'linkedin', 'whatsapp'],
      show_copy_link: true,
      button_style: 'filled',
    },
  },
  {
    type: 'lp_text',
    label: 'Text Block',
    description: 'A heading and paragraph you write yourself or let AI draft',
    icon: 'type',
    aiGenerated: false,
    defaultProps: {
      heading: 'Section Heading',
      body: 'Write your copy here, or use “Regenerate with AI”.',
      align: 'left',
      max_width: 'narrow',
      // Dark text on the default (white) background so copy is readable out of the
      // box. Change background + text colour via the Section background control.
      _text_color: '#0f172a',
    },
  },
  {
    type: 'lp_effect',
    label: 'Visual Effect',
    description: 'Animated background effect with an editable text overlay',
    icon: 'sparkles',
    aiGenerated: false,
    defaultProps: {
      // effect_id MUST be a real manifest id; 'snow' is canvas2d (no three/lottie).
      effect_id: 'snow',
      effect_props: {},
      // Dark section background by default so light-coloured effects (Snow, Particles,
      // etc. default to white/light) and the white overlay text are visible out of the
      // box. Editable via the Section background control in the properties panel.
      _bg_type: 'solid',
      _bg_color: '#0b1020',
      overlay_heading: 'Your Headline Here',
      overlay_subheading: 'A supporting line that sits above the effect.',
      overlay_show_cta: false,
      overlay_cta_text: '',
      overlay_cta_url: '#lead-form',
      overlay_cta_color: '#2177FF',
      overlay_cta_align: 'center',
      overlay_align: 'center',
      overlay_valign: 'center',
      overlay_color: '#ffffff',
      min_height: 480,
    },
  },
];

// ---------------------------------------------------------------------------
// 6. Default AI generation order
// ---------------------------------------------------------------------------

/**
 * The component types the AI generates by default, in page order.
 * lp_testimonials and lp_quiz are included conditionally based on
 * the user's choices in the landing page creation wizard.
 */
export const DEFAULT_LP_SECTION_TYPES: LPComponentType[] = [
  'lp_hero',
  'lp_features',
  'lp_services',
  'lp_stats',
  'lp_process',
  'lp_lead_form',
  'lp_faq',
  'lp_cta',
];
